import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import TurbineList from "./TurbineList";
import ScopeOfWork from "./ScopeOfWork";
import ModalAddTurbine from "./ModalAddTurbine";
import ModalAddWindFarm from "./ModalAddWindFarm";
import ModalAddScope from "./ModalAddScope";
import ModalAddModel from "./ModalAddModel";
import ModalAddType from "./ModalAddType";
import TurbineDetailsModal from "./TurbineDetailsModal";
import ModalSaveAllInst from "./ModalSaveAllInst";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const TurbineManager = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [turbines, setTurbines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workPermitIdFromUrl, setWorkPermitIdFromUrl] = useState(null);
  const [regions, setRegions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterWindFarm, setFilterWindFarm] = useState("");
  const [workScopes, setWorkScopes] = useState([]);
  const [selectedWorkScope, setSelectedWorkScope] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [instToView, setInstToView] = useState(null);

  const workpermitId = searchParams.get("workpermitId");
  const turbineId = searchParams.get("turbineId");
  const workScopeId = searchParams.get("workScopeId");
  const instructionId = searchParams.get("instructionId");

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const addModelButtonRef = useRef(null);

  // Stan określający, który modal jest otwarty
  const [openModal, setOpenModal] = useState(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) setOpenModal(null); // Zamknij wszystkie modale przy otwieraniu menu
  };

  useEffect(() => {
    if (instructionId) {
      const instructionIdNum = Number(instructionId);
      const foundInstruction = instructions.find(inst => inst.id === instructionIdNum);
      if (foundInstruction) setInstToView(foundInstruction);
      else setInstToView(null);
    } else {
      setInstToView(null);
    }
  }, [instructions, instructionId]);

  const fetchTurbines = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/turbines/`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`Błąd: ${response.status}`);
      const data = await response.json();
      const updatedTurbines = data.map((turbine, index) => ({
        id: turbine.id,
        number: turbine.turbine_number || `T-${index + 1}`,
        name: turbine.name || "No data",
        model: turbine.model_data.name || "No data",
        modelId: turbine.model_data.id || "No data",
        type: turbine.type_data.name || "No data",
        typeId: turbine.type_data.id || "No data",
        location: turbine.location || "No data",
        windFarm: turbine.wind_farm_data?.name || "No data",
        windFarmId: turbine.wind_farm_data?.id || "No data",
        region: turbine.wind_farm_data?.region || "No data",
        height: turbine.height || "No data",
        power: turbine.power || "No data",
        manufacturer: turbine.model_data.manufacturer || "No data",
        isSelected: turbineId && turbine.id === Number(turbineId),
      }));
      setTurbines(updatedTurbines);
    } catch (err) {
      console.error("Błąd w fetchTurbines:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/regions/`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`Błąd: ${response.status}`);
      const data = await response.json();
      setRegions(data);
    } catch (err) {
      console.error("Błąd w fetchRegions:", err);
    }
  };

  const fetchScopes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/scopeofworks/`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`Błąd: ${response.status}`);
      const data = await response.json();
      setWorkScopes(data);
      if (workScopeId) {
        const workScopeIdNum = Number(workScopeId);
        const foundWorkScope = data.find(scope => scope.id === workScopeIdNum);
        if (foundWorkScope) setSelectedWorkScope(foundWorkScope);
      }
    } catch (err) {
      console.error("Błąd w fetchScopes:", err);
    }
  };

  const fetchInstructions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/instructions-latest/`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`Błąd: ${response.status}`);
      const data = await response.json();
      setInstructions(data);
    } catch (err) {
      console.error("Błąd w fetchInstructions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchTurbines();
      await fetchScopes();
      await fetchInstructions();
      await fetchRegions();
    };
    initializeData();
    setWorkPermitIdFromUrl(workpermitId);
  }, [instructionId]);

  // Funkcje obsługujące modale
  const handleOpenModal = () => {
    setOpenModal(openModal === "turbine" ? null : "turbine");
  };

  const [editTurbine, setEditTurbine] = useState(null);

  const handleEditTurbine = (turbine) => {
    setIsDetailsModalOpen(false);
    setEditTurbine(turbine);
    setOpenModal("turbine");
    setIsMenuOpen(true);
  };

  const handleCloseModal = async () => {
    setOpenModal(null);
    await fetchTurbines();
    if (editTurbine) openDetailsModal(editTurbine);
    setEditTurbine(null);
  };

  const handleOpenScopeModal = () => {
    setOpenModal(openModal === "scope" ? null : "scope");
  };

  const handleCloseScopeModal = async () => {
    setOpenModal(null);
    await fetchScopes();
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setTurbines((prevTurbines) =>
      prevTurbines.map((turbine) => ({ ...turbine, isSelected: newSelectAll }))
    );
  };

  const handleSelectTurbine = (id) => {
    setTurbines((prevTurbines) =>
      prevTurbines.map((turbine) =>
        turbine.id === id ? { ...turbine, isSelected: !turbine.isSelected } : turbine
      )
    );
  };

  const handleOpenWindFarmModal = () => {
    setOpenModal(openModal === "windfarm" ? null : "windfarm");
  };

  const handleCloseWindFarmModal = async () => {
    setOpenModal(null);
    await fetchRegions();
  };

  const handleSave = () => alert(`Pomyślnie dodano.`);

  const handleOpenModelModal = () => {
    setOpenModal(openModal === "model" ? null : "model");
  };

  const handleCloseModelModal = async () => {
    setOpenModal(null);
    await fetchTurbines();
  };

  const handleOpenTypeModal = () => {
    setOpenModal(openModal === "type" ? null : "type");
  };

  const handleCloseTypeModal = async () => {
    setOpenModal(null);
    await fetchTurbines();
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const scopeRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scopeRef.current && !scopeRef.current.contains(event.target)) setIsExpanded(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [detailedTurbine, setDetailedTurbine] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const openDetailsModal = async (turbine) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/turbines/${turbine.id}/`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`Błąd: ${response.status}`);
      const data = await response.json();
      const updatedTurbine = {
        id: data.id,
        number: data.turbine_number || "No data",
        name: data.name || "No data",
        model: data.model_data?.name || "No data",
        modelId: data.model_data?.id || "No data",
        type: data.type_data?.name || "No data",
        typeId: data.type_data?.id || "No data",
        location: data.location || "No data",
        windFarm: data.wind_farm_data?.name || "No data",
        windFarmId: data.wind_farm_data?.id || "No data",
        region: data.wind_farm_data?.region || "No data",
        height: data.height || "No data",
        power: data.power || "No data",
        manufacturer: data.model_data?.manufacturer || "No data",
      };
      const turbineInstructions = (instructions || []).filter(
        (instr) => instr.turbine_data.id === updatedTurbine.id
      );
      setDetailedTurbine({ ...updatedTurbine, instructions: turbineInstructions || [] });
      setIsDetailsModalOpen(true);
    } catch (err) {
      console.error("Błąd podczas otwierania szczegółów turbiny:", err.message);
      alert("Nie udało się pobrać szczegółów turbiny. Spróbuj ponownie.");
    }
  };

  const closeDetailsModal = () => {
    setDetailedTurbine(null);
    setIsDetailsModalOpen(false);
    fetchTurbines();
    fetchInstructions();
  };

  const [activeView, setActiveView] = useState("ScopeOfWork");
  const [selectedInstructions, setSelectedInstructions] = useState([]);
  const handleSaveInstructions = (insToSave) => {
    setSelectedInstructions(insToSave);
    setActiveView("ModalSaveAllInst");
    closeDetailsModal();
  };

  const onSaveAll = () => {
    fetchTurbines();
    fetchInstructions();
    setActiveView("ScopeOfWork");
  };

  return (
    <div className="flex flex-col h-screen bg-steel-50 overflow-hidden">
      {/* Header Section */}
      <div className="bg-white border-b border-steel-200 p-3 flex items-center justify-between">
        <div className="flex items-center">
          <Link to={workPermitIdFromUrl ? `/ecc/approve?workpermit_id=${workPermitIdFromUrl}` : "/ecc"}>
            <button
              className="p-2 mr-4 rounded-full text-graphite-600 hover:bg-steel-100"
              aria-label={workPermitIdFromUrl ? "Back to Work Permit" : "Go back"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-left h-5 w-5"
              >
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
            </button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-graphite-900">Turbine Management</h1>
            <p className="text-sm text-graphite-500">Manage turbines, models, and work scopes</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={toggleMenu}
            className="px-4 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600 flex items-center gap-2"
            aria-label="Open menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14"/>
              <path d="M5 12h14"/>
            </svg>
            Add New
          </button>
        </div>
      </div>

      {/* Sliding Menu (from the right) - styled like wind company sidebar */}
      <div
        className={`fixed top-0 right-0 h-full bg-white border-l border-slate-200 shadow-xl transform transition-transform duration-300 ease-in-out z-50 w-80 flex flex-col ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header - like sidebar brand header */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-slate-100">
            <span className="font-black text-slate-900 text-sm">Add New</span>
            <button
              onClick={toggleMenu}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation - like sidebar nav */}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
            {/* Add Turbine */}
            <div>
              <button
                onClick={handleOpenModal}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
                  openModal === "turbine"
                    ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v4"/>
                  <path d="m6.34 6.34 2.83 2.83"/>
                </svg>
                <span className="text-sm font-bold">Add Turbine</span>
              </button>
              {openModal === "turbine" && (
                <div className="mt-1 ml-6 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <ModalAddTurbine
                    isOpen={true}
                    onClose={handleCloseModal}
                    turbine={editTurbine}
                    isEmbedded={true}
                    onOpenModelModal={handleOpenModelModal}
                    regions={regions}
                  />
                </div>
              )}
            </div>

            {/* Add Model */}
            <div>
              <button
                ref={addModelButtonRef}
                onClick={handleOpenModelModal}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
                  openModal === "model"
                    ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                </svg>
                <span className="text-sm font-bold">Add Model</span>
              </button>
              {openModal === "model" && (
                <div className="mt-1 ml-6 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <ModalAddModel
                    isOpen={true}
                    onClose={handleCloseModelModal}
                    onSave={handleCloseModelModal}
                    isEmbedded={true}
                  />
                </div>
              )}
            </div>

            {/* Add Type */}
            <div>
              <button
                onClick={handleOpenTypeModal}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
                  openModal === "type"
                    ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M4 7V4h16v3"/>
                  <path d="M9 20h6"/>
                  <path d="M12 4v16"/>
                </svg>
                <span className="text-sm font-bold">Add Type</span>
              </button>
              {openModal === "type" && (
                <div className="mt-1 ml-6 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <ModalAddType
                    isOpen={true}
                    onClose={handleCloseTypeModal}
                    onSave={handleCloseTypeModal}
                    isEmbedded={true}
                  />
                </div>
              )}
            </div>

            {/* Add Windfarm */}
            <div>
              <button
                onClick={handleOpenWindFarmModal}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
                  openModal === "windfarm"
                    ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M17.7 7.7a7.5 7.5 0 1 0 0 8.6"/>
                  <circle cx="12" cy="12" r="2"/>
                  <path d="M12 2v4"/>
                  <path d="M12 18v4"/>
                </svg>
                <span className="text-sm font-bold">Add Windfarm</span>
              </button>
              {openModal === "windfarm" && (
                <div className="mt-1 ml-6 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <ModalAddWindFarm
                    isOpen={true}
                    onClose={handleCloseWindFarmModal}
                    onSave={handleSave}
                    regions={regions}
                    isEmbedded={true}
                  />
                </div>
              )}
            </div>

            {/* Add Scope of Work */}
            <div>
              <button
                onClick={handleOpenScopeModal}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
                  openModal === "scope"
                    ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M9 11l3 3L22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
                <span className="text-sm font-bold">Add Scope of Work</span>
              </button>
              {openModal === "scope" && (
                <div className="mt-1 ml-6 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <ModalAddScope
                    isOpen={true}
                    onClose={handleCloseScopeModal}
                    onSave={fetchScopes}
                    isEmbedded={true}
                  />
                </div>
              )}
            </div>
          </nav>

          {/* Footer - like sidebar footer */}
          <div className="p-2 mt-auto border-t border-slate-100">
            <button
              onClick={toggleMenu}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M18 6 6 18"/>
                <path d="m6 6 12 12"/>
              </svg>
              <span className="text-sm font-bold">Close</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for background dimming when menu is open */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-graphite-950/50 z-40"
          onClick={toggleMenu}
        ></div>
      )}

      {/* Main Content Section */}
      <div className="flex-1 flex mt-3 ml-2 mr-2 gap-4 overflow-auto">
        <div className={`transition-all duration-300 ${isExpanded ? "w-1/4" : "w-1/2"}`}>
          <TurbineList
            turbines={turbines}
            regions={regions}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            filterType={filterType}
            setFilterType={setFilterType}
            filterWindFarm={filterWindFarm}
            setFilterWindFarm={setFilterWindFarm}
            selectAll={selectAll}
            handleSelectAll={handleSelectAll}
            handleSelectTurbine={handleSelectTurbine}
            selectedWorkScope={selectedWorkScope}
            instructions={instructions}
            setInstToView={setInstToView}
            openDetailsModal={openDetailsModal}
          />
        </div>

        <div ref={scopeRef} className={`transition-all duration-300 ${isExpanded ? "w-3/4" : "w-1/2"}`}>
          {activeView === "ScopeOfWork" ? (
            <ScopeOfWork
              workScopes={workScopes}
              selectedWorkScope={selectedWorkScope}
              setSelectedWorkScope={setSelectedWorkScope}
              instructions={instructions}
              fetchInstructions={fetchInstructions}
              handleInstructionChange={(rowIndex, colIndex, value) => {
                const updatedInstructions = [...instructions];
                updatedInstructions[rowIndex][colIndex] = value;
                setInstructions(updatedInstructions);
              }}
              addInstructionRow={() => setInstructions((prev) => [...prev, Array(6).fill("")])}
              handleSaveAsDefault={() => alert(`Zakres "${workScopes}" zapisano jako domyślny.`)}
              handleSaveAsTarget={() => alert(`Zakres "${workScopes}" zapisano jako docelowy.`)}
              selectedTurbines={turbines.filter((turbine) => turbine.isSelected).map((turbine) => turbine.id)}
              setSelectedTurbines={(ids) => {
                const turbineIds = Array.isArray(ids) ? ids : [];
                setTurbines((prevTurbines) =>
                  prevTurbines.map((turbine) => ({
                    ...turbine,
                    isSelected: turbineIds.includes(turbine.id),
                  }))
                );
              }}
              instToView={instToView}
              setInstToView={setInstToView}
              setIsExpanded={setIsExpanded}
            />
          ) : (
            <ModalSaveAllInst
              onSave={onSaveAll}
              onCancel={() => setActiveView("ScopeOfWork")}
              insToSave={selectedInstructions}
              selectedTurbines={turbines.filter((turbine) => turbine.isSelected).map((turbine) => turbine.id)}
              setSelectedTurbines={(ids) => {
                const turbineIds = Array.isArray(ids) ? ids : [];
                setTurbines((prevTurbines) =>
                  prevTurbines.map((turbine) => ({
                    ...turbine,
                    isSelected: turbineIds.includes(turbine.id),
                  }))
                );
              }}
            />
          )}
        </div>
      </div>

      {/* Only keep the TurbineDetailsModal outside */}
      {isDetailsModalOpen && detailedTurbine && (
        <TurbineDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
          turbine={detailedTurbine}
          onEdit={handleEditTurbine}
          onSaveInstructions={handleSaveInstructions}
        />
      )}
    </div>
  );
};

export default TurbineManager;